import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import AppShell from './components/AppShell';
import ImpersonationBanner from './components/ImpersonationBanner';

const MAINTENANCE = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

function MaintenanceBanner() {
  if (!MAINTENANCE) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9998,
      background: '#e65100', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '7px 20px', fontSize: 13, fontWeight: 500,
      boxShadow: '0 2px 6px rgba(0,0,0,.2)',
    }}>
      🔧 <strong style={{ marginLeft: 6 }}>Maintenance mode is ON</strong>
      <span style={{ marginLeft: 8, fontWeight: 400, opacity: 0.9 }}>— vendors cannot access the portal right now</span>
    </div>
  );
}
import { LoginPage, RegisterPage } from './pages/Auth';
import ResetPasswordPage from './pages/ResetPassword';
import AdminDashboard from './pages/AdminDashboard';
import { AdminProfiles, AdminProfileDetail } from './pages/AdminProfiles';
import { VendorDashboard, VendorProfileDetail } from './pages/VendorDashboard';
import ProfileForm from './pages/ProfileForm';
import './index.css';
import AdminUsers from './pages/AdminUsers';

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  const isImpersonating = !!sessionStorage.getItem('awwt_imp_token');

  if (loading) return (
    <div className="loading" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;

  // During impersonation a superadmin may access vendor routes — skip role gate
  if (role && !isImpersonating) {
    if (user.role !== role && !(role === 'admin' && user.role === 'superadmin')) {
      return <Navigate to={user.role === 'vendor' ? '/vendor' : '/admin'} replace />;
    }
  }

  return (
    <>
      <MaintenanceBanner />
      <ImpersonationBanner />
      <AppShell style={{ paddingTop: isImpersonating ? (MAINTENANCE ? 80 : 40) : (MAINTENANCE ? 40 : 0) }}>
        {children}
      </AppShell>
    </>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  const isImpersonating = !!sessionStorage.getItem('awwt_imp_token');
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // During impersonation go to vendor view
  if (isImpersonating) return <Navigate to="/vendor" replace />;
  return <Navigate to={user.role === 'vendor' ? '/vendor' : '/admin'} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/" element={<RootRedirect />} />
          <Route path="/vendor" element={<ProtectedRoute role="vendor"><VendorDashboard /></ProtectedRoute>} />
          <Route path="/vendor/profiles" element={<ProtectedRoute role="vendor"><VendorDashboard /></ProtectedRoute>} />
          <Route path="/vendor/profiles/:id" element={<ProtectedRoute role="vendor"><VendorProfileDetail /></ProtectedRoute>} />
          <Route path="/vendor/profiles/:id/edit" element={<ProtectedRoute role="vendor"><ProfileForm /></ProtectedRoute>} />
          <Route path="/vendor/new" element={<ProtectedRoute role="vendor"><ProfileForm /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/profiles" element={<ProtectedRoute role="admin"><AdminProfiles /></ProtectedRoute>} />
          <Route path="/admin/profiles/new" element={<ProtectedRoute role="admin"><ProfileForm /></ProtectedRoute>} />
          <Route path="/admin/profiles/:id" element={<ProtectedRoute role="admin"><AdminProfileDetail /></ProtectedRoute>} />
          <Route path="/admin/expiring" element={<ProtectedRoute role="admin"><AdminProfiles filterExpiring={true} /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute role="admin"><AdminUsers /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
